import { saveAs } from 'file-saver';

/**
 * Конвертирует буфер в строку base64.
 *
 * @param {any} buffer - Буфер, который нужно конвертировать.
 * @returns {string} - Строка в формате base64, созданная из буфера.
 */
export const bufferToBase64 = (buffer: ArrayBuffer): string => {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;

  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }

  return window.btoa(binary);
};

/**
 * Функция `calculateFileSizeInMB` вычисляет размер файла в мегабайтах (MB), исходя из его размера в байтах.
 *
 * @param sizeInBytes - Размер файла в байтах, который требуется преобразовать в мегабайты.
 * @returns Строка, представляющая размер файла в мегабайтах с округлением до двух знаков после запятой.
 *
 * @example
 * ```typescript
 * const fileSize = calculateFileSizeInMB(10485760); // "10.00"
 * ```
 */
export const calculateFileSizeInMB = (sizeInBytes: number): string => {
  const sizeInMB = sizeInBytes / (1024 * 1024);
  return sizeInMB.toFixed(2);
};

/**
 * Конвертирует файл в строку base64.
 *
 * @param {File} file - Файл, который нужно конвертировать.
 * @returns {Promise<string>} - Промис, который разрешается в строку base64.
 */
export const convertFileToBase64 = async (file: File): Promise<string[]> => {
  try {
    const base64String = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        resolve(reader.result as string);
      };
      reader.onerror = (error) => {
        reject(error);
      };
      reader.readAsDataURL(file);
    });
    return base64String.split(',');
  } catch {
    return ['', ''];
  }
};

/**
 * Сохраняет строку base64 как PDF-файл.
 *
 * @param {string} base64String - Строка в формате base64, представляющая PDF.
 * @param {string} fileName - Имя файла для сохранения.
 */
export const saveBase64AsPDF = (base64String: string, fileName: string) => {
  const byteCharacters = atob(base64String);
  const byteNumbers = new Array(byteCharacters.length);

  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  const byteArray = new Uint8Array(byteNumbers);
  const pdfBlob = new Blob([byteArray], { type: 'application/pdf' });

  saveAs(pdfBlob, fileName);
};
